/**
 * StaffNoteChangeCard - Rich card display for a single staff note's changes.
 *
 * Staff note layout using shared change utilities:
 * - Header with note content preview
 * - Key fields (created_at, modified_at)
 * - Agent tags as pills
 * - Collapsible reference IDs section (user_id)
 * - Amber change indicators with old-value tooltips
 * - Transfer checkboxes + "Mark as Transferred" button
 */

import { StickyNote } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { ChangedStaffNoteEntity } from '@/services/api/generated/crossMatterChangesService';

import {
  buildCheckableItems,
  type CheckableFieldConfig,
} from '../utils/buildCheckableItems';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { CollapsibleSection } from './CollapsibleSection';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EntityField } from './EntityField';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TransferButton } from './TransferButton';
import { TransferCheckbox } from './TransferCheckbox';

// ============================================================================
// Staff note config
// ============================================================================

const KEY_FIELDS: FieldConfig[] = [
  { key: 'created_at', label: 'Created At', isDate: true },
  { key: 'modified_at', label: 'Modified At', isDate: true },
];

const REFERENCE_FIELDS: { key: string; label: string }[] = [
  { key: 'user_id', label: 'User ID' },
];

const STAFF_NOTE_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'content', type: 'field' },
  { key: 'created_at', type: 'field' },
  { key: 'modified_at', type: 'field' },
  { key: 'user_id', type: 'field' },
  { key: 'agent_tags', type: 'field' },
];

// ============================================================================
// Main Component
// ============================================================================

export const StaffNoteChangeCard: React.FC<{
  entity: ChangedStaffNoteEntity;
}> = ({ entity }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  const checkableItems = React.useMemo<string[]>(
    () =>
      buildCheckableItems(
        entity.change_type,
        displayData as Record<string, unknown> | undefined,
        entity.events,
        changes,
        STAFF_NOTE_CHECKABLE_FIELDS,
      ),
    [entity.change_type, entity.events, changes, displayData],
  );

  const checklist = useTransferChecklist(entity.change_type, checkableItems);
  const transferEventIds = React.useMemo(() => entity.events.map((e) => e.id), [entity.events]);
  const transfer = useTransferAction(transferEventIds);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Content
  const {content} = displayData;
  const contentChanged = changes.isFieldChanged('content');

  // Agent tags
  const agentTags = displayData.agent_tags ?? [];
  const agentTagsChanged = changes.isFieldChanged('agent_tags');

  // Reference fields visibility
  const visibleReferenceFields = REFERENCE_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );
  const changedReferenceFields = visibleReferenceFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedReferenceFields = visibleReferenceFields.filter(
    (f) => !changes.isFieldChanged(f.key),
  );

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  // For CREATED/DELETED, always show content in body since header truncates it
  const showContentInBody =
    entity.change_type === 'CREATED' || entity.change_type === 'DELETED';

  const hasExpandedContent =
    !!content ||
    contentChanged ||
    agentTagsChanged ||
    KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue || changes.isFieldChanged(f.key);
    }) ||
    visibleReferenceFields.length > 0 ||
    isUpdatedWithoutDiffs;

  // Unchanged content for ShowMoreSection
  const hasUnchangedContent =
    (entity.change_type === 'UPDATED' || entity.change_type === 'SAME') &&
    ((!contentChanged && !!content) ||
      (!agentTagsChanged && agentTags.length > 0) ||
      KEY_FIELDS.some((f) => {
        const val = data[f.key];
        const hasValue = val !== null && val !== undefined && val !== '';
        return hasValue && !changes.isFieldChanged(f.key);
      }) ||
      unchangedReferenceFields.length > 0);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={entity.name}
      description={entity.description}
      icon={StickyNote}
      iconBgClassName="bg-amber-50"
      iconTextClassName="text-amber-600"
      badges={
        agentTags.length > 0 ? (
          <>
            {agentTags.map((tag) => (
              <Badge key={tag} color="zinc" className="text-[10px] shrink-0">
                {tag}
              </Badge>
            ))}
          </>
        ) : undefined
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={checklist}
      hasExpandedContent={hasExpandedContent}
    >
      {/* Events fallback */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist
          events={entity.events}
          checklist={checklist}
        />
      )}

      {/* Content — full display for CREATED/DELETED (header truncates) */}
      {showContentInBody && !!content && (
        <div>
          <div className="flex items-center gap-1 mb-0.5">
            <span className="text-xs font-medium text-gray-500">Content</span>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
        </div>
      )}

      {/* Content — changed */}
      {!showContentInBody && contentChanged && (
        <ChangedFieldHighlight
          label="Content"
          fieldKey="content"
          changes={changes}
          checklist={checklist}
        >
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {content || '\u2014'}
          </p>
        </ChangedFieldHighlight>
      )}

      {/* Agent tags — changed */}
      {agentTagsChanged && (
        <ChangedFieldHighlight
          label="Agent Tags"
          fieldKey="agent_tags"
          changes={changes}
          checklist={checklist}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            {agentTags.length > 0 ? (
              agentTags.map((tag) => (
                <Badge key={tag} color="zinc" className="text-[10px]">
                  {tag}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-gray-400">{'\u2014'}</span>
            )}
          </div>
        </ChangedFieldHighlight>
      )}

      {/* Key fields grid */}
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        checklist={checklist}
        changeType={entity.change_type}
      />

      {/* Reference IDs (changed only) */}
      {changedReferenceFields.length > 0 && (
        <CollapsibleSection
          title="References"
          count={changedReferenceFields.length}
          changeType={entity.change_type}
          hasChanges
        >
          {changedReferenceFields.map((f) => (
            <div key={f.key} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <EntityField
                  label={f.label}
                  value={data[f.key]}
                  fieldKey={f.key}
                  changes={changes}
                />
              </div>
              {checklist.showCheckboxes && changes.isFieldChanged(f.key) && (
                <TransferCheckbox
                  checked={checklist.isChecked(f.key)}
                  onToggle={() => checklist.toggle(f.key)}
                  className="mt-2.5"
                />
              )}
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* Show More Section for unchanged content */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Content — unchanged */}
          {!contentChanged && !!content && (
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-xs font-medium text-gray-500">
                  Content
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {content}
              </p>
            </div>
          )}

          {/* Agent tags — unchanged */}
          {!agentTagsChanged && agentTags.length > 0 && (
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-xs font-medium text-gray-500">
                  Agent Tags
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {agentTags.map((tag) => (
                  <Badge key={tag} color="zinc" className="text-[10px]">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Unchanged key fields */}
          <KeyFieldsGridUnchanged
            fields={KEY_FIELDS}
            data={data}
            changes={changes}
            checklist={checklist}
          />

          {/* Unchanged reference IDs */}
          {unchangedReferenceFields.length > 0 && (
            <CollapsibleSection
              title="References"
              count={unchangedReferenceFields.length}
              changeType={entity.change_type}
              hasChanges={false}
            >
              {unchangedReferenceFields.map((f) => (
                <div key={f.key} className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <EntityField
                      label={f.label}
                      value={data[f.key]}
                      fieldKey={f.key}
                      changes={changes}
                    />
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}
        </ShowMoreSection>
      )}

      {/* Transfer button */}
      {checklist.showButton && (
        <TransferButton
          enabled={checklist.buttonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};
